/**
 * M4 — Ajánlatok oldal (Quotes)
 * Standard+ ('ajanlat') feature
 */
import { useState, useEffect, useCallback } from 'react';
import { get, post, patch, del } from '../../lib/api';
import {
  PageHeader, Button, Badge, Card, Input, Modal, EmptyState, LoadingOverlay,
} from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { useFeature } from '../../hooks/useFeature';
import LockedFeature from '../../components/LockedFeature';
import ActionsDropdown from '../../components/ActionsDropdown';
import PartnerAutocomplete from '../../components/PartnerAutocomplete';
import MoneyInput from '../../components/ui/MoneyInput';
import Select from '../../components/ui/Select';
import ModulumBadge from '../../components/ui/ModulumBadge';

// ── Típusok ──────────────────────────────────────────────────────────────────

interface Quote {
  id: string; quote_number: string; status: string;
  partner_id?: string; partner_name?: string; partner_name_snapshot?: string; partner_network_tenant_id?: string | null;
  subtotal_net: number; subtotal_vat: number; subtotal_gross: number;
  discount_percent: number; currency: string; tema: string;
  issue_date: string; expiry_date?: string;
  sent_at?: string; converted_at?: string; sales_order_id?: string;
  created_at: string;
}

interface QuoteSection {
  id: string; section_type: 'FIX' | 'VALASZTHATO_CSOPORT' | 'OPCIONALIS';
  title: string; description?: string; sort_order: number;
  lines: QuoteLine[];
}

interface QuoteLine {
  id: string; section_id: string; sort_order: number;
  item_name_snapshot: string; item_desc_snapshot?: string;
  quantity: number; unit: string; unit_price_net: number; vat_rate: string;
  line_net: number; line_vat: number; line_gross: number;
  line_discount_percent: number; is_recommended: boolean;
  public_note?: string; default_selected: boolean;
  purchase_price_net?: number; margin_percent?: number;
  price_source: string;
}

interface QuoteDetail extends Quote {
  sections: QuoteSection[];
  notes?: string; intro_text?: string; footer_text?: string;
  price_list_id?: string; valid_days: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_MAP: Record<string, { label: string; variant: 'default' | 'info' | 'success' | 'error' | 'warning' }> = {
  draft:     { label: 'Vázlat', variant: 'default' },
  sent:      { label: 'Kiküldve', variant: 'info' },
  viewed:    { label: 'Megtekintve', variant: 'info' },
  accepted:  { label: 'Elfogadva', variant: 'success' },
  declined:  { label: 'Elutasítva', variant: 'error' },
  expired:   { label: 'Lejárt', variant: 'warning' },
  converted: { label: 'Konvertálva', variant: 'success' },
};

const TEMA_COLORS: Record<string, string> = {
  kek: '#2563eb', zold: '#16a34a', bordo: '#9f1239', narancs: '#ea580c', szurke: '#4b5563',
};

function fmtDate(iso: string | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
}
function fmtMoney(n: number) { return Math.round(n).toLocaleString('hu-HU') + ' Ft'; }
function fmtPct(n: number) { return n.toFixed(1).replace('.', ',') + '%'; }

const SECTION_TYPE_LABELS: Record<string, string> = {
  FIX: 'Fix (kötelező)', VALASZTHATO_CSOPORT: 'Választható csoport', OPCIONALIS: 'Opcionális',
};

// ── Főoldal ───────────────────────────────────────────────────────────────────

export default function QuotesPage() {
  const { active: hasQuotes } = useFeature('ajanlat');

  if (!hasQuotes) return (
    <div style={{ padding: 32 }}>
      <PageHeader title="Ajánlatok" />
      <LockedFeature feature="ajanlat" />
    </div>
  );

  return <QuotesContent />;
}

function QuotesContent() {
  const { toast } = useToast();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState<QuoteDetail | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-01-01`;
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      const res = await get<{ data: Quote[]; total: number }>(`/quotes?${params}`);
      setQuotes(res.data); setTotal(res.total);
    } catch { toast('Betöltési hiba.', 'error'); }
    finally { setLoading(false); }
  }, [statusFilter, dateFrom, dateTo, page, pageSize, toast]);

  useEffect(() => { loadQuotes(); }, [loadQuotes]);

  async function openDetail(id: string) {
    try {
      const q = await get<QuoteDetail>(`/quotes/${id}`);
      setSelectedQuote(q);
    } catch { toast('Betöltési hiba.', 'error'); }
  }

  async function handleSend(id: string) {
    try {
      await post(`/quotes/${id}/send`, {});
      toast('Ajánlat kiküldve, megosztási link generálva.', 'success');
      loadQuotes();
      if (selectedQuote?.id === id) openDetail(id);
    } catch (e: unknown) {
      toast((e as { message?: string })?.message || 'Küldési hiba.', 'error');
    }
  }

  async function handleConvert(id: string) {
    try {
      const res = await post<{ orderNumber: string }>(`/quotes/${id}/convert`, {});
      toast(`Megrendelés létrehozva: ${res.orderNumber}`, 'success');
      loadQuotes();
      setSelectedQuote(null);
    } catch (e: unknown) {
      toast((e as { message?: string })?.message || 'Konverzió sikertelen.', 'error');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Biztosan törli a vázlatot?')) return;
    try {
      await del(`/quotes/${id}`);
      toast('Ajánlat törölve.', 'success');
      loadQuotes();
      if (selectedQuote?.id === id) setSelectedQuote(null);
    } catch (e: unknown) {
      toast((e as { message?: string })?.message || 'Törlési hiba.', 'error');
    }
  }

  return (
    <div style={{ padding: '0 0 64px' }}>
      <LoadingOverlay visible={loading} />
      <PageHeader
        title="Ajánlatok"
        actions={<Button variant="primary" onClick={() => setShowNew(true)}>+ Új ajánlat</Button>}
      />

      {/* Szűrők */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Select
            label="Státusz"
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            options={[
              { value: '', label: 'Összes' },
              { value: 'draft', label: 'Vázlat' },
              { value: 'sent', label: 'Kiküldve' },
              { value: 'viewed', label: 'Megtekintve' },
              { value: 'accepted', label: 'Elfogadva' },
              { value: 'declined', label: 'Elutasítva' },
              { value: 'expired', label: 'Lejárt' },
              { value: 'converted', label: 'Konvertálva' },
            ]}
          />
          <Input label="Dátumtól" type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} />
          <Input label="Dátumig" type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} />
        </div>
      </Card>

      {/* Lista */}
      {quotes.length === 0 && !loading ? (
        <EmptyState
          title="Nincs ajánlat"
          description="Hozd létre az első ajánlatot az + Új ajánlat gombbal."
          action={<Button variant="primary" onClick={() => setShowNew(true)}>+ Új ajánlat</Button>}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {quotes.map(q => (
            <Card
              key={q.id}
              style={{ cursor: 'pointer' }}
              onClick={() => openDetail(q.id)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Téma szín */}
                <div style={{
                  width: 4, height: 48, borderRadius: 4,
                  background: TEMA_COLORS[q.tema] || '#4b5563', flexShrink: 0,
                }} />

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{q.quote_number}</span>
                    <Badge variant={STATUS_MAP[q.status]?.variant ?? 'default'}>
                      {STATUS_MAP[q.status]?.label ?? q.status}
                    </Badge>
                    {q.discount_percent > 0 && (
                      <Badge variant="warning">-{fmtPct(q.discount_percent)} kedv.</Badge>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2, display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                    {q.partner_network_tenant_id && <ModulumBadge />}
                    {q.partner_name_snapshot || q.partner_name || 'Névtelen partner'}
                    {' · '}{fmtDate(q.issue_date)}
                    {q.expiry_date && ` · Lejár: ${fmtDate(q.expiry_date)}`}
                  </div>
                </div>

                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontWeight: 600 }}>{fmtMoney(q.subtotal_gross)}</div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>{fmtMoney(q.subtotal_net)} nettó</div>
                </div>

                <ActionsDropdown items={[
                  { label: 'Megnyitás', onClick: () => openDetail(q.id) },
                  ...(q.status === 'draft' ? [{ label: 'Kiküldés', onClick: () => handleSend(q.id) }] : []),
                  ...(q.status === 'sent' ? [{ label: 'Kiküldés újra', onClick: () => handleSend(q.id) }] : []),
                  ...(['accepted', 'sent', 'viewed'].includes(q.status) ? [{ label: 'Konvertálás megrendelésre', onClick: () => handleConvert(q.id) }] : []),
                  ...(q.status === 'draft' ? [{ label: 'Törlés', onClick: () => handleDelete(q.id), danger: true }] : []),
                ]} />
              </div>
            </Card>
          ))}

          {/* Lapozás */}
          {total > pageSize && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
              <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Előző</Button>
              <span style={{ alignSelf: 'center', fontSize: 13 }}>{page} / {Math.ceil(total / pageSize)}</span>
              <Button variant="secondary" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)}>Következő</Button>
            </div>
          )}
        </div>
      )}

      {/* Új ajánlat modal */}
      {showNew && (
        <NewQuoteModal
          onClose={() => setShowNew(false)}
          onCreate={() => { setShowNew(false); loadQuotes(); }}
        />
      )}

      {/* Detail oldalpanel */}
      {selectedQuote && (
        <QuoteDetailPanel
          quote={selectedQuote}
          onClose={() => setSelectedQuote(null)}
          onReload={() => openDetail(selectedQuote.id)}
          onSend={() => handleSend(selectedQuote.id)}
          onConvert={() => handleConvert(selectedQuote.id)}
          onDelete={() => handleDelete(selectedQuote.id)}
        />
      )}
    </div>
  );
}

// ── Új ajánlat modal ─────────────────────────────────────────────────────────

function NewQuoteModal({ onClose, onCreate }: { onClose: () => void; onCreate: () => void }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [partner, setPartner] = useState<{ id: string; name: string } | null>(null);
  const [partnerPriceListId, setPartnerPriceListId] = useState<string | null>(null);
  const [tema, setTema] = useState<'kek' | 'zold' | 'bordo' | 'narancs' | 'szurke'>('kek');
  const [validDays, setValidDays] = useState(30);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [notes, setNotes] = useState('');
  const [introText, setIntroText] = useState('');

  async function handlePartnerChange(p: { id: string; name: string } | null) {
    setPartner(p);
    setPartnerPriceListId(null);
    if (p) {
      get<any>(`/partners/${p.id}`).then(data => setPartnerPriceListId(data?.price_list_id || null)).catch(() => {});
    }
  }

  async function handleCreate() {
    setLoading(true);
    try {
      await post('/quotes', {
        partnerId: partner?.id || undefined,
        priceListId: partnerPriceListId || undefined,
        tema, validDays, discountPercent, discountCeiling: 20,
        notes: notes || undefined,
        introText: introText || undefined,
      });
      toast('Ajánlat létrehozva.', 'success');
      onCreate();
    } catch (e: unknown) {
      toast((e as { message?: string })?.message || 'Létrehozási hiba.', 'error');
    } finally { setLoading(false); }
  }

  return (
    <Modal isOpen={true} title="Új ajánlat" onClose={onClose}>
      <LoadingOverlay visible={loading} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
        <PartnerAutocomplete
          label="Partner (opcionális)"
          value={partner}
          onChange={handlePartnerChange}
        />

        <div>
          <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>Téma szín</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {(Object.entries(TEMA_COLORS) as [string, string][]).map(([t, color]) => (
              <button
                key={t}
                onClick={() => setTema(t as typeof tema)}
                style={{
                  width: 32, height: 32, borderRadius: '50%', background: color, border: 'none',
                  cursor: 'pointer', outline: tema === t ? `3px solid ${color}` : 'none',
                  outlineOffset: 2,
                }}
                title={t}
              />
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <Input
              label="Érvényesség (nap)"
              type="number"
              value={String(validDays)}
              onChange={e => setValidDays(Math.max(1, parseInt(e.target.value) || 30))}
            />
          </div>
          <div style={{ flex: 1 }}>
            <MoneyInput
              label="Kedvezmény %"
              value={discountPercent}
              onChange={setDiscountPercent}
             
            />
          </div>
        </div>

        <Input
          label="Üdvözlő szöveg (megjelenik az ajánlaton)"
          value={introText}
          onChange={e => setIntroText(e.target.value)}
          placeholder="Pl. Köszönjük az érdeklődést!"
        />

        <Input
          label="Belső megjegyzés (nem látszik az ügyfélnek)"
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>Mégsem</Button>
          <Button variant="primary" onClick={handleCreate}>Létrehozás</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Ajánlat részletes nézet ───────────────────────────────────────────────────

function QuoteDetailPanel({
  quote, onClose, onReload, onSend, onConvert, onDelete,
}: {
  quote: QuoteDetail;
  onClose: () => void;
  onReload: () => void;
  onSend: () => void;
  onConvert: () => void;
  onDelete: () => void;
}) {
  const { toast } = useToast();
  const [showAddSection, setShowAddSection] = useState(false);
  const [showAddLine, setShowAddLine] = useState<string | null>(null); // sectionId
  const [shareLink, setShareLink] = useState<string | null>(null);
  const isEditable = ['draft', 'sent'].includes(quote.status);

  async function handleGetShare() {
    try {
      const res = await post<{ share: { token: string } }>(`/quotes/${quote.id}/send`, {});
      const token = res.share?.token;
      if (token) {
        const url = `${window.location.origin}/public/ajanlat/${token}`;
        setShareLink(url);
        navigator.clipboard.writeText(url).catch(() => {});
        toast('Link másolva a vágólapra.', 'success');
      }
      onReload();
    } catch (e: unknown) {
      toast((e as { message?: string })?.message || 'Hiba.', 'error');
    }
  }

  async function handleDeleteSection(sectionId: string) {
    if (!confirm('Törli a szekciót az összes tétellel?')) return;
    try {
      await del(`/quotes/${quote.id}/sections/${sectionId}`);
      toast('Szekció törölve.', 'success'); onReload();
    } catch { toast('Törlési hiba.', 'error'); }
  }

  async function handleDeleteLine(lineId: string) {
    try {
      await del(`/quotes/${quote.id}/lines/${lineId}`);
      toast('Sor törölve.', 'success'); onReload();
    } catch { toast('Törlési hiba.', 'error'); }
  }

  const accentColor = TEMA_COLORS[quote.tema] || '#4b5563';

  return (
    <Modal
      isOpen={true}
      title={`${quote.quote_number} — ${quote.partner_name_snapshot || quote.partner_name || 'Névtelen'}`}
      onClose={onClose}
      size="lg"
    >
      {/* Fejléc adatok */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20, padding: '12px 16px', background: '#f9fafb', borderRadius: 8 }}>
        <div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Státusz</div>
          <Badge variant={STATUS_MAP[quote.status]?.variant ?? 'default'}>
            {STATUS_MAP[quote.status]?.label ?? quote.status}
          </Badge>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Kiállítva</div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{fmtDate(quote.issue_date)}</div>
        </div>
        {quote.expiry_date && (
          <div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Lejárat</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: new Date(quote.expiry_date) < new Date() ? '#ef4444' : undefined }}>
              {fmtDate(quote.expiry_date)}
            </div>
          </div>
        )}
        <div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Nettó összeg</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{fmtMoney(quote.subtotal_net)}</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Bruttó összeg</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: accentColor }}>{fmtMoney(quote.subtotal_gross)}</div>
        </div>
        {quote.discount_percent > 0 && (
          <div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Kedvezmény</div>
            <Badge variant="warning">-{fmtPct(quote.discount_percent)}</Badge>
          </div>
        )}
        {/* Haszonkulcs (seller-only) */}
        {quote.sections.some(s => s.lines.some(l => l.margin_percent != null)) && (
          <div style={{ marginLeft: 'auto', padding: '4px 10px', background: '#f0fdf4', borderRadius: 6, fontSize: 12, color: '#16a34a' }}>
            Átl. marzs: {fmtPct(
              quote.sections.flatMap(s => s.lines)
                .filter(l => l.margin_percent != null)
                .reduce((acc, l, _, arr) => acc + l.margin_percent! / arr.length, 0)
            )}
          </div>
        )}
      </div>

      {/* Akciók */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {quote.status === 'draft' && (
          <Button variant="primary" onClick={onSend}>Kiküldés + link generálás</Button>
        )}
        {quote.status === 'sent' && (
          <Button variant="secondary" onClick={handleGetShare}>Új link generálás</Button>
        )}
        {['accepted', 'sent', 'viewed'].includes(quote.status) && (
          <Button variant="primary" onClick={onConvert}>Konvertálás megrendelésre</Button>
        )}
        {quote.status === 'draft' && (
          <Button variant="danger" onClick={onDelete}>Törlés</Button>
        )}
      </div>

      {shareLink && (
        <div style={{ padding: '10px 14px', background: '#eff6ff', borderRadius: 8, fontSize: 13, marginBottom: 16, wordBreak: 'break-all' }}>
          <strong>Megosztási link (vágólapra másolva):</strong><br />{shareLink}
        </div>
      )}

      {/* Szekciók */}
      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Szekciók és tételek</h3>
          {isEditable && (
            <Button variant="secondary" size="sm" onClick={() => setShowAddSection(true)}>+ Szekció</Button>
          )}
        </div>

        {quote.sections.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: 14, padding: '16px 0' }}>
            Még nincs szekció. Adj hozzá szekciót, majd tételeket.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {quote.sections.map(sec => (
              <div key={sec.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                {/* Szekció fejléc */}
                <div style={{
                  padding: '10px 14px', background: '#f9fafb',
                  borderBottom: sec.lines.length ? '1px solid #e5e7eb' : undefined,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{sec.title}</span>
                    <span style={{ marginLeft: 8 }}>
                      <Badge variant="default">{SECTION_TYPE_LABELS[sec.section_type] || sec.section_type}</Badge>
                    </span>
                    {sec.description && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{sec.description}</div>}
                  </div>
                  {isEditable && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button variant="secondary" size="sm" onClick={() => setShowAddLine(sec.id)}>+ Tétel</Button>
                      <Button variant="secondary" size="sm" onClick={() => handleDeleteSection(sec.id)}>Törlés</Button>
                    </div>
                  )}
                </div>

                {/* Tételsorok */}
                {sec.lines.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f3f4f6', color: '#6b7280' }}>
                        <th style={{ padding: '6px 12px', textAlign: 'left' }}>Megnevezés</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Mennyiség</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Nettó ár</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Kedv.</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Nettó sor</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>ÁFA</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Bruttó</th>
                        {isEditable && <th style={{ padding: '6px 8px' }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {sec.lines.map((l, i) => (
                        <tr key={l.id} style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : undefined }}>
                          <td style={{ padding: '8px 12px' }}>
                            <div style={{ fontWeight: 500 }}>
                              {l.is_recommended && <span style={{ color: accentColor, marginRight: 4 }}>★</span>}
                              {l.item_name_snapshot}
                            </div>
                            {l.item_desc_snapshot && <div style={{ fontSize: 12, color: '#9ca3af' }}>{l.item_desc_snapshot}</div>}
                            {l.margin_percent != null && (
                              <div style={{ fontSize: 11, color: '#16a34a' }}>Marzs: {fmtPct(l.margin_percent)}</div>
                            )}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{l.quantity} {l.unit}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{fmtMoney(l.unit_price_net)}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>
                            {l.line_discount_percent > 0 ? `-${fmtPct(l.line_discount_percent)}` : '—'}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{fmtMoney(l.line_net)}</td>
                          <td style={{ padding: '8px', textAlign: 'right', color: '#6b7280' }}>{fmtMoney(l.line_vat)}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{fmtMoney(l.line_gross)}</td>
                          {isEditable && (
                            <td style={{ padding: '8px' }}>
                              <Button variant="secondary" size="sm" onClick={() => handleDeleteLine(l.id)}>✕</Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Szekció hozzáadás modal */}
      {showAddSection && (
        <AddSectionModal
          quoteId={quote.id}
          onClose={() => setShowAddSection(false)}
          onAdd={() => { setShowAddSection(false); onReload(); }}
        />
      )}

      {/* Sor hozzáadás modal */}
      {showAddLine && (
        <AddLineModal
          quoteId={quote.id}
          sectionId={showAddLine}
          onClose={() => setShowAddLine(null)}
          onAdd={() => { setShowAddLine(null); onReload(); }}
        />
      )}
    </Modal>
  );
}

// ── Szekció hozzáadás ─────────────────────────────────────────────────────────

function AddSectionModal({ quoteId, onClose, onAdd }: { quoteId: string; onClose: () => void; onAdd: () => void }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [sectionType, setSectionType] = useState<'FIX' | 'VALASZTHATO_CSOPORT' | 'OPCIONALIS'>('FIX');
  const [description, setDescription] = useState('');

  async function handleAdd() {
    if (!title.trim()) { toast('A cím kötelező.', 'error'); return; }
    setLoading(true);
    try {
      await post(`/quotes/${quoteId}/sections`, { sectionType, title: title.trim(), description: description || undefined, sortOrder: 0 });
      toast('Szekció hozzáadva.', 'success'); onAdd();
    } catch (e: unknown) {
      toast((e as { message?: string })?.message || 'Hiba.', 'error');
    } finally { setLoading(false); }
  }

  return (
    <Modal isOpen={true} title="Szekció hozzáadása" onClose={onClose}>
      <LoadingOverlay visible={loading} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input label="Cím" value={title} onChange={e => setTitle(e.target.value)} placeholder="pl. Alaptermékek" />
        <Select
          label="Szekció típusa"
          value={sectionType}
          onChange={e => setSectionType(e.target.value as typeof sectionType)}
          options={[
            { value: 'FIX', label: 'Fix (kötelező tételek)' },
            { value: 'VALASZTHATO_CSOPORT', label: 'Választható csoport (1 elem)' },
            { value: 'OPCIONALIS', label: 'Opcionális (ügyfél dönti el)' },
          ]}
        />
        <Input label="Leírás (opcionális)" value={description} onChange={e => setDescription(e.target.value)} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>Mégsem</Button>
          <Button variant="primary" onClick={handleAdd}>Hozzáadás</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Tételsor hozzáadás ────────────────────────────────────────────────────────

function AddLineModal({ quoteId, sectionId, onClose, onAdd }: {
  quoteId: string; sectionId: string; onClose: () => void; onAdd: () => void;
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [qty, setQty] = useState(1);
  const [unit, setUnit] = useState('db');
  const [unitPrice, setUnitPrice] = useState(0);
  const [vatRate, setVatRate] = useState('27%');
  const [discountPct, setDiscountPct] = useState(0);
  const [purchasePrice, setPurchasePrice] = useState<number | null>(null);
  const [isRecommended, setIsRecommended] = useState(false);
  const [publicNote, setPublicNote] = useState('');

  async function handleAdd() {
    if (!name.trim()) { toast('A megnevezés kötelező.', 'error'); return; }
    if (unitPrice <= 0) { toast('Az ár kötelező.', 'error'); return; }
    setLoading(true);
    try {
      await post(`/quotes/${quoteId}/lines`, {
        sectionId, sortOrder: 0,
        itemNameSnapshot: name.trim(),
        quantity: qty, unit, unitPriceNet: unitPrice,
        vatRate, lineDiscountPercent: discountPct,
        purchasePriceNet: purchasePrice ?? undefined,
        isRecommended, publicNote: publicNote || undefined,
        defaultSelected: true,
      });
      toast('Tétel hozzáadva.', 'success'); onAdd();
    } catch (e: unknown) {
      toast((e as { message?: string })?.message || 'Hiba.', 'error');
    } finally { setLoading(false); }
  }

  const bruttoPreview = unitPrice * qty * (1 - discountPct / 100) * 1.27;
  const marginPct = purchasePrice && unitPrice > 0 ? ((unitPrice - purchasePrice) / unitPrice) * 100 : null;

  return (
    <Modal isOpen={true} title="Tétel hozzáadása" onClose={onClose}>
      <LoadingOverlay visible={loading} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input label="Megnevezés" value={name} onChange={e => setName(e.target.value)} />

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <MoneyInput label="Mennyiség" value={qty} onChange={setQty} />
          </div>
          <div style={{ flex: 1 }}>
            <Input label="Egység" value={unit} onChange={e => setUnit(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 2 }}>
            <MoneyInput label="Nettó egységár (Ft)" value={unitPrice} onChange={setUnitPrice} />
          </div>
          <div style={{ flex: 1 }}>
            <Select
              label="ÁFA kulcs"
              value={vatRate}
              onChange={e => setVatRate(e.target.value)}
              options={[
                { value: '27%', label: '27%' },
                { value: '18%', label: '18%' },
                { value: '5%', label: '5%' },
                { value: '0%', label: '0%' },
                { value: 'TAM', label: 'TAM' },
                { value: 'AAM', label: 'AAM' },
              ]}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <MoneyInput label="Kedvezmény %" value={discountPct} onChange={setDiscountPct} />
          </div>
          <div style={{ flex: 1 }}>
            <MoneyInput
              label="Bészerzési ár (Ft) — csak neked"
              value={purchasePrice ?? 0}
              onChange={v => setPurchasePrice(v || null)}
            />
          </div>
        </div>

        {/* Profit preview */}
        <div style={{ padding: '8px 12px', background: '#f9fafb', borderRadius: 8, fontSize: 13, display: 'flex', gap: 16 }}>
          <span>Bruttó sor: <strong>{fmtMoney(bruttoPreview)}</strong></span>
          {marginPct !== null && (
            <span style={{ color: marginPct >= 0 ? '#16a34a' : '#ef4444' }}>
              Marzs: <strong>{fmtPct(marginPct)}</strong>
            </span>
          )}
        </div>

        <Input label="Publikus megjegyzés (látszik az ügyfélnek)" value={publicNote} onChange={e => setPublicNote(e.target.value)} />

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={isRecommended} onChange={e => setIsRecommended(e.target.checked)} />
          Ajánlott badge (★ csillag a tételnél)
        </label>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>Mégsem</Button>
          <Button variant="primary" onClick={handleAdd}>Hozzáadás</Button>
        </div>
      </div>
    </Modal>
  );
}
